// SDK hooks for the 4 HR app config tables (migration 0196 + 0189):
//   hr_company_settings   — I6 SettingsPage
//   hr_email_templates    — I3 EmailTemplatesPage
//   hr_agents             — I5 AgentsPage
//   hr_prompt_overrides   — I12 PromptsPage (+ hr_prompt_override_versions history)
//
// All hooks are tenant-scoped and rely on RLS; never pass service-role keys.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// biome-ignore lint/suspicious/noExplicitAny: dynamic supabase client signature
type AnyClient = { from: (t: string) => any };

// ─── hr_company_settings ─────────────────────────────────────────────────────

export interface HrCompanySettings {
  tenant_id: string;
  company_name: string;
  company_website: string | null;
  website: string | null;
  logo_url: string | null;
  industry: 'technology' | 'finance' | 'hospitality' | 'healthcare' | null;
  company_size: '1_10' | '11_50' | '51_200' | '201_500' | null;
  timezone: string;
  data_retention_days: number;
  notification_prefs: Record<string, boolean>;
  auto_approve_rules: Array<{ id: string; limitGel: number; vacancy: string; enabled: boolean }>;
  agent_config: { agents: unknown[]; daily_limit: number };
  updated_at: string;
  updated_by: string | null;
}

export type UpdateHrCompanySettingsInput = Partial<
  Omit<HrCompanySettings, 'tenant_id' | 'updated_at' | 'updated_by'>
>;

export const hrCompanySettingsKeys = {
  all: ['hr-company-settings'] as const,
  byTenant: (tenantId: string) => [...hrCompanySettingsKeys.all, tenantId] as const,
};

export function useHrCompanySettings(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: hrCompanySettingsKeys.byTenant(tenantId),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as AnyClient)
        .from('hr_company_settings')
        .select('*')
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as HrCompanySettings | null;
    },
  });
}

export function useUpdateHrCompanySettings(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateHrCompanySettingsInput) => {
      const { data, error } = await (supabase as unknown as AnyClient)
        .from('hr_company_settings')
        .upsert({ tenant_id: tenantId, ...input }, { onConflict: 'tenant_id' })
        .select()
        .single();
      if (error) throw error;
      return data as HrCompanySettings;
    },
    onSuccess: (data) => {
      qc.setQueryData(hrCompanySettingsKeys.byTenant(tenantId), data);
    },
  });
}

// ─── hr_email_templates ──────────────────────────────────────────────────────

export interface HrEmailTemplate {
  id: string;
  tenant_id: string;
  slug: string | null;
  name: string;
  trigger_event: 'shortlisted' | 'rejected' | 'invited' | 'custom';
  subject: string;
  body: string;
  updated_at: string;
  last_edited_by: string | null;
  created_at: string | null;
}

export interface SaveHrEmailTemplateInput {
  id?: string;
  slug?: string | null;
  name: string;
  trigger_event: HrEmailTemplate['trigger_event'];
  subject: string;
  body: string;
}

export const hrEmailTemplateKeys = {
  all: ['hr-email-templates'] as const,
  byTenant: (tenantId: string) => [...hrEmailTemplateKeys.all, tenantId] as const,
};

export function useHrEmailTemplates(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: hrEmailTemplateKeys.byTenant(tenantId),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as AnyClient)
        .from('hr_email_templates')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as HrEmailTemplate[];
    },
  });
}

export function useSaveHrEmailTemplate(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveHrEmailTemplateInput) => {
      const payload = { ...input, tenant_id: tenantId };
      const query = (supabase as unknown as AnyClient).from('hr_email_templates');
      const { data, error } = input.id
        ? await query.update(payload).eq('id', input.id).eq('tenant_id', tenantId).select().single()
        : await query.insert(payload).select().single();
      if (error) throw error;
      return data as HrEmailTemplate;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: hrEmailTemplateKeys.byTenant(tenantId) });
    },
  });
}

export function useDeleteHrEmailTemplate(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as unknown as AnyClient)
        .from('hr_email_templates')
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
      return { id };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: hrEmailTemplateKeys.byTenant(tenantId) });
    },
  });
}

// ─── hr_agents ───────────────────────────────────────────────────────────────

export type HrAgentId = 'cost' | 'parser' | 'matching' | 'recommendation';
export type HrAgentModel = 'claude-haiku-4-5' | 'claude-sonnet-4-6';

export interface HrAgent {
  id: string;
  tenant_id: string;
  agent_id: HrAgentId;
  enabled: boolean;
  model: HrAgentModel | null;
  max_pages: number | null;
  scoring_weights: { skills: number; experience: number; education: number } | null;
  anonymized_mode: boolean | null;
  daily_limit: number;
  updated_at: string;
  updated_by: string | null;
}

export interface UpsertHrAgentInput {
  agent_id: HrAgentId;
  enabled?: boolean;
  model?: HrAgentModel | null;
  max_pages?: number | null;
  scoring_weights?: HrAgent['scoring_weights'];
  anonymized_mode?: boolean | null;
  daily_limit?: number;
}

export const hrAgentKeys = {
  all: ['hr-agents'] as const,
  byTenant: (tenantId: string) => [...hrAgentKeys.all, tenantId] as const,
};

export function useHrAgents(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: hrAgentKeys.byTenant(tenantId),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as AnyClient)
        .from('hr_agents')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('agent_id');
      if (error) throw error;
      return (data ?? []) as HrAgent[];
    },
  });
}

export function useUpsertHrAgent(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertHrAgentInput) => {
      const { data, error } = await (supabase as unknown as AnyClient)
        .from('hr_agents')
        .upsert({ tenant_id: tenantId, ...input }, { onConflict: 'tenant_id,agent_id' })
        .select()
        .single();
      if (error) throw error;
      return data as HrAgent;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: hrAgentKeys.byTenant(tenantId) });
    },
  });
}

// ─── hr_prompt_overrides ─────────────────────────────────────────────────────

export type HrPromptId =
  | 'parser'
  | 'matcher'
  | 'recommender'
  | 'jd_generator'
  | 'cv_self_check'
  | 'cv_tailoring';
export type HrPromptStatus = 'active' | 'draft';

export interface HrPromptOverride {
  id: string;
  tenant_id: string;
  prompt_id: HrPromptId;
  status: HrPromptStatus;
  current_version: number;
  text: string;
  last_updated: string;
  created_by: string | null;
}

export interface HrPromptOverrideVersion {
  id: string;
  prompt_override_id: string;
  version: number;
  text: string;
  deployed_at: string;
  created_by: string | null;
}

export interface SaveHrPromptOverrideInput {
  prompt_id: HrPromptId;
  text: string;
  status?: HrPromptStatus;
}

export const hrPromptKeys = {
  all: ['hr-prompt-overrides'] as const,
  byTenant: (tenantId: string) => [...hrPromptKeys.all, tenantId] as const,
  versions: (overrideId: string) => [...hrPromptKeys.all, 'versions', overrideId] as const,
};

export function useHrPromptOverrides(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: hrPromptKeys.byTenant(tenantId),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as AnyClient)
        .from('hr_prompt_overrides')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('prompt_id');
      if (error) throw error;
      return (data ?? []) as HrPromptOverride[];
    },
  });
}

export function useHrPromptOverrideVersions(
  supabase: SupabaseClientLike,
  overrideId: string | null,
) {
  return useQuery({
    queryKey: hrPromptKeys.versions(overrideId ?? ''),
    enabled: Boolean(overrideId),
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as AnyClient)
        .from('hr_prompt_override_versions')
        .select('*')
        .eq('prompt_override_id', overrideId)
        .order('version', { ascending: false });
      if (error) throw error;
      return (data ?? []) as HrPromptOverrideVersion[];
    },
  });
}

export function useSaveHrPromptOverride(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveHrPromptOverrideInput) => {
      // Read existing to bump current_version when text changes.
      const { data: existing } = await (supabase as unknown as AnyClient)
        .from('hr_prompt_overrides')
        .select('id,current_version,text')
        .eq('tenant_id', tenantId)
        .eq('prompt_id', input.prompt_id)
        .maybeSingle();

      const textChanged = !existing || existing.text !== input.text;
      const nextVersion = textChanged
        ? (existing?.current_version ?? 0) + 1
        : existing.current_version;

      const { data: upserted, error } = await (supabase as unknown as AnyClient)
        .from('hr_prompt_overrides')
        .upsert(
          {
            tenant_id: tenantId,
            prompt_id: input.prompt_id,
            text: input.text,
            status: input.status ?? 'draft',
            current_version: nextVersion,
            last_updated: new Date().toISOString(),
          },
          { onConflict: 'tenant_id,prompt_id' },
        )
        .select()
        .single();
      if (error) throw error;

      if (textChanged) {
        const { error: vErr } = await (supabase as unknown as AnyClient)
          .from('hr_prompt_override_versions')
          .insert({
            prompt_override_id: upserted.id,
            version: nextVersion,
            text: input.text,
          });
        if (vErr) throw vErr;
      }

      return upserted as HrPromptOverride;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: hrPromptKeys.byTenant(tenantId) });
    },
  });
}
